import React, { useEffect, useMemo, useState } from 'react';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { formatPriceAmountToString } from 'libs/end-to-end/src/currencies';
import { PayBenchmarkingCompensationFigures } from 'libs/end-to-end/src/pay-benchmarking';
import cn from 'classnames';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { PayBenchmarkingCompensationGraphViewVariant } from 'apps/sigma2-ui/src/pay-benchmarking/components/PayBenchmarkingCompensationGraphViewVariant';
import { PayBenchmarkingCompensationGraphViewVariants } from 'apps/sigma2-ui/src/pay-benchmarking/components/PayBenchmarkingCompensationGraphViewVariant';

interface PayBenchmarkingCompensationGraphViewProps {
  currency: string;
  compensationFigures: PayBenchmarkingCompensationFigures;
  variant: PayBenchmarkingCompensationGraphViewVariant;
  className?: string;
}

const calculatePredictedRangeBounds = ({
  marketRangeLowerBound,
  marketRangeUpperBound,
  internalPayBandLowerBound,
  internalPayBandUpperBound,
  suggestedTotalCompensation,
}: {
  marketRangeLowerBound: number;
  marketRangeUpperBound: number;
  internalPayBandLowerBound: number;
  internalPayBandUpperBound: number;
  suggestedTotalCompensation: number;
}) => {
  const lowestBoundValue = Math.min(
    marketRangeLowerBound,
    internalPayBandLowerBound,
  );
  const highestBoundValue = Math.max(
    marketRangeUpperBound,
    internalPayBandUpperBound,
  );

  // figure out where the graph should start and end so that totalCompensation is in the middle of the graph, and the lowestBoundValue is at or inside the left edge of the graph, and the highestBoundValue is at or inside the right edge of the graph
  const graphStart = Math.min(
    lowestBoundValue,
    suggestedTotalCompensation -
      (highestBoundValue - suggestedTotalCompensation),
  );

  const graphEnd = Math.max(
    highestBoundValue,
    suggestedTotalCompensation +
      (suggestedTotalCompensation - lowestBoundValue),
  );

  const internalPayBandLeft =
    ((internalPayBandLowerBound - graphStart) / (graphEnd - graphStart)) * 100;
  const internalPayBandWidth =
    ((internalPayBandUpperBound - internalPayBandLowerBound) /
      (graphEnd - graphStart)) *
    100;

  const marketPayBandLeft =
    ((marketRangeLowerBound - graphStart) / (graphEnd - graphStart)) * 100;
  const marketPayBandWidth =
    ((marketRangeUpperBound - marketRangeLowerBound) /
      (graphEnd - graphStart)) *
    100;

  return {
    lowestBoundValue,
    highestBoundValue,
    graphStart,
    graphEnd,
    internalPayBandLeft,
    internalPayBandWidth,
    marketPayBandLeft,
    marketPayBandWidth,
  };
};

export const PayBenchmarkingCompensationGraphView = (
  props: PayBenchmarkingCompensationGraphViewProps,
) => {
  const {
    marketRangeLowerBound,
    marketRangeUpperBound,
    internalPayBandLowerBound,
    internalPayBandUpperBound,
    suggestedTotalCompensation,
  } = props.compensationFigures;

  const { isHoverOnGraphEnabled, isPayBandValuesDisplayed } =
    PayBenchmarkingCompensationGraphViewVariants[props.variant];

  const { currency } = props;

  const {
    lowestBoundValue,
    highestBoundValue,
    graphStart,
    graphEnd,
    internalPayBandLeft,
    internalPayBandWidth,
    marketPayBandLeft,
    marketPayBandWidth,
  } = calculatePredictedRangeBounds({
    marketRangeLowerBound,
    marketRangeUpperBound,
    internalPayBandLowerBound,
    internalPayBandUpperBound,
    suggestedTotalCompensation,
  });

  const displayedInternalLowerBound = formatPriceAmountToString({
    amount: internalPayBandLowerBound,
    currency: currency,
  });

  const displayedInternalUpperBound = formatPriceAmountToString({
    amount: internalPayBandUpperBound,
    currency: currency,
  });

  const displayedMarketLowerBound = formatPriceAmountToString({
    amount: marketRangeLowerBound,
    currency: currency,
  });

  const displayedMarketUpperBound = formatPriceAmountToString({
    amount: marketRangeUpperBound,
    currency: currency,
  });

  const displayedRemainingLowerBoundValue = formatPriceAmountToString({
    amount: graphStart,
    currency: currency,
  });

  const displayedRemainingUpperBoundValue = formatPriceAmountToString({
    amount: graphEnd,
    currency: currency,
  });

  const displayedInternalRange = `${displayedInternalLowerBound} - ${displayedInternalUpperBound}`;
  const displayedMarketRange = `${displayedMarketLowerBound} - ${displayedMarketUpperBound}`;

  const [showMarketHover, setShowMarketHover] = React.useState(false);
  const [showInternalHover, setShowInternalHover] = React.useState(false);

  const marketLabelsRef = React.useRef<HTMLDivElement>(null);
  const [marketLabelsWidth, setMarketLabelsWidth] = useState<number | null>(
    null,
  );
  const internalLabelsRef = React.useRef<HTMLDivElement>(null);
  const [internalLabelsWidth, setInternalLabelsWidth] = useState<number | null>(
    null,
  );

  const isMarketLabelsVisible = useMemo(() => {
    if (!isPayBandValuesDisplayed) {
      return false;
    }

    if (!marketLabelsWidth) {
      return true;
    }

    return marketLabelsWidth > 200;
  }, [isPayBandValuesDisplayed, marketLabelsWidth]);

  const isInternalLabelsVisible = useMemo(() => {
    if (!isPayBandValuesDisplayed) {
      return false;
    }

    if (!internalLabelsWidth) {
      return true;
    }

    return internalLabelsWidth > 200;
  }, [internalLabelsWidth, isPayBandValuesDisplayed]);

  const isMarketGraphHoverable = useMemo(() => {
    return isHoverOnGraphEnabled && !isMarketLabelsVisible;
  }, [isHoverOnGraphEnabled, isMarketLabelsVisible]);

  const isInternalGraphHoverable = useMemo(() => {
    return isHoverOnGraphEnabled && !isInternalLabelsVisible;
  }, [isHoverOnGraphEnabled, isInternalLabelsVisible]);

  const isLowerBoundRemainingSpaceTextVisible = useMemo(() => {
    if (!isPayBandValuesDisplayed) {
      return false;
    }

    if (lowestBoundValue - graphStart > 0) {
      return true;
    } else if (
      lowestBoundValue === internalPayBandLowerBound &&
      !isInternalLabelsVisible
    ) {
      return true;
    } else if (
      lowestBoundValue === marketRangeLowerBound &&
      !isMarketLabelsVisible
    ) {
      return true;
    } else {
      return false;
    }
  }, [
    graphStart,
    internalPayBandLowerBound,
    isInternalLabelsVisible,
    isMarketLabelsVisible,
    isPayBandValuesDisplayed,
    lowestBoundValue,
    marketRangeLowerBound,
  ]);

  const isUpperBoundRemainingSpaceTextVisible = useMemo(() => {
    if (!isPayBandValuesDisplayed) {
      return false;
    }

    if (graphEnd - highestBoundValue > 0) {
      return true;
    } else if (
      highestBoundValue === internalPayBandUpperBound &&
      !isInternalLabelsVisible
    ) {
      return true;
    } else if (
      highestBoundValue === marketRangeUpperBound &&
      !isMarketLabelsVisible
    ) {
      return true;
    } else {
      return false;
    }
  }, [
    graphEnd,
    highestBoundValue,
    internalPayBandUpperBound,
    isInternalLabelsVisible,
    isMarketLabelsVisible,
    isPayBandValuesDisplayed,
    marketRangeUpperBound,
  ]);

  useEffect(() => {
    if (!isPayBandValuesDisplayed) {
      return;
    }
    setMarketLabelsWidth(marketLabelsRef.current?.clientWidth ?? null);
    setInternalLabelsWidth(internalLabelsRef.current?.clientWidth ?? null);
    window.addEventListener('resize', () => {
      if (!marketLabelsRef.current || !internalLabelsRef.current) {
        return;
      }
      uiAppLogger.info('newWidth', marketLabelsRef.current?.clientWidth);
      setMarketLabelsWidth(marketLabelsRef.current?.clientWidth ?? null);
      setInternalLabelsWidth(internalLabelsRef.current?.clientWidth ?? null);
    });
    return () => {
      window.removeEventListener('resize', () => {});
    };
  }, [isPayBandValuesDisplayed]);

  const hoverView = (title: string, displayedRange: string) => {
    return (
      <div className="absolute left-4 top-1 w-[8rem] pointer-events-none">
        <div className="z-tooltip p-2 rounded-xl bg-white bg-opacity-95">
          <Paragraph textColor="text-black" className="font-bold">
            {title}
          </Paragraph>
          <Paragraph textColor="text-black">{displayedRange}</Paragraph>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className={cn('rounded-3xl', props.className)}>
        <div className="relative py-2 px-1 h-full">
          <div className="relative h-full">
            <div className="absolute h-full rounded-3xl bg-royal-800 bg-opacity-75 left-[0%] w-full" />
            <div
              className={`absolute y-0 h-full rounded-3xl bg-violet-900 items-center`}
              style={{
                left: `${marketPayBandLeft}%`,
                width: `${marketPayBandWidth}%`,
              }}
            >
              {showMarketHover && hoverView('Pay Band', displayedMarketRange)}
              <div
                className={`h-full w-full`}
                onMouseEnter={() => {
                  if (isMarketGraphHoverable) {
                    setShowMarketHover(true);
                  }
                }}
                onMouseLeave={() => {
                  setShowMarketHover(false);
                }}
              />
            </div>
            <div
              className={`absolute h-full top-1/2 left-0 transform -translate-y-1/2 rounded-3xl bg-violet-900 bg-opacity-75 outline outline-white/75 outline-2 items-center`}
              style={{
                left: `${internalPayBandLeft}%`,
                width: `${internalPayBandWidth}%`,
              }}
            >
              <div
                className={`relative flex justify-between h-full w-full items-center`}
                onMouseEnter={() => {
                  if (isInternalGraphHoverable) {
                    setShowInternalHover(true);
                  }
                }}
                onMouseLeave={() => {
                  setShowInternalHover(false);
                }}
              >
                {showInternalHover &&
                  hoverView('Pay Band', displayedInternalRange)}
                <div
                  ref={internalLabelsRef}
                  className={cn(
                    `flex justify-between h-full w-full items-center px-2`,
                    isInternalLabelsVisible && 'visible',
                    !isInternalLabelsVisible && 'invisible',
                  )}
                >
                  <Paragraph textColor="text-white" className="font-bold">
                    {displayedInternalLowerBound}
                  </Paragraph>
                  <Paragraph textColor="text-white" className="font-bold">
                    {displayedInternalUpperBound}
                  </Paragraph>
                </div>
              </div>
            </div>
            {!showInternalHover && !showMarketHover && (
              <div className="absolute bg-white rounded-2xl z-tooltip h-3 w-3 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            )}
          </div>
        </div>
        <div className="h-[4rem] relative flex items-center p-2">
          <div
            className="absolute h-full bg-transparent"
            style={{
              left: `${marketPayBandLeft}%`,
              width: `${marketPayBandWidth}%`,
            }}
          >
            <div className="w-full">
              <div
                ref={marketLabelsRef}
                className={cn(
                  `flex justify-between items-center`,
                  isMarketLabelsVisible && 'visible',
                  !isMarketLabelsVisible && 'invisible',
                )}
              >
                <Paragraph textColor="text-blue-800" className="font-bold">
                  {displayedMarketLowerBound}
                </Paragraph>
                <Paragraph textColor="text-blue-800" className="font-bold">
                  {displayedMarketUpperBound}
                </Paragraph>
              </div>
            </div>
          </div>
          <div
            className="absolute h-full"
            style={{
              left: `0%`,
              width: `100%`,
            }}
          >
            <div className="w-full">
              <div
                className={cn(
                  `grid grid-flow-col w-full items-center justify-stretch`,
                )}
              >
                {isLowerBoundRemainingSpaceTextVisible && (
                  <Paragraph
                    textColor="text-gray-600"
                    className="font-bold justify-self-start"
                  >
                    {displayedRemainingLowerBoundValue}
                  </Paragraph>
                )}
                {isUpperBoundRemainingSpaceTextVisible && (
                  <Paragraph
                    textColor="text-gray-600"
                    className="font-bold justify-self-end"
                  >
                    {displayedRemainingUpperBoundValue}
                  </Paragraph>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
